// Design System FormSection Component
// Unified form section container

import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Typography,
  Tooltip,
  IconButton,
  Stack,
  useTheme,
  styled
} from '@mui/material';
import { HelpOutline as HelpIcon } from '@mui/icons-material';

// Import design tokens
import { spacingTokens, colorUtils } from '../../tokens';

// Styled components using design tokens
const StyledCard = styled(Card)(({ theme, variant, elevate }) => {
  const mode = theme.palette.mode;
  
  const baseStyles = {
    marginBottom: spacingTokens.layout.lg,
    borderRadius: spacingTokens.component.lg,
    transition: 'all 0.3s ease',
    border: `1px solid ${colorUtils.withOpacity(theme.palette.divider, mode === 'dark' ? 0.1 : 0.15)}`,
    backgroundColor: mode === 'dark' 
      ? colorUtils.withOpacity(theme.palette.background.paper, 0.6)
      : theme.palette.background.paper,
  };
  
  const variantStyles = {
    outlined: {
      boxShadow: 'none',
      '&:hover': elevate ? {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[2],
      } : {},
    },
    elevated: {
      boxShadow: mode === 'dark' 
        ? `0 2px 8px ${colorUtils.withOpacity(theme.palette.common.black, 0.1)}`
        : `0 2px 8px ${colorUtils.withOpacity(theme.palette.common.black, 0.04)}`,
      '&:hover': elevate ? {
        transform: 'translateY(-2px)',
        boxShadow: theme.shadows[4],
      } : {},
    },
  };
  
  return {
    ...baseStyles,
    ...variantStyles[variant || 'outlined'],
  };
});

const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
  paddingBottom: spacingTokens.component.sm,
  '& .MuiCardHeader-title': {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  '& .MuiCardHeader-subheader': {
    marginTop: spacingTokens.component.xs,
  },
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  paddingTop: spacingTokens.layout.md,
  paddingBottom: spacingTokens.layout.md,
  '& .MuiFormControl-root': {
    marginBottom: spacingTokens.layout.sm,
  },
  '&:last-child': {
    paddingBottom: spacingTokens.layout.md,
  },
}));

/**
 * FormSection - Unified form section container
 * 
 * Features:
 * - Consistent styling with design system
 * - Support for title, description, and tooltips
 * - Optional icons and dividers
 * - Hover effects and elevation
 * - Responsive design
 */
const FormSection = ({
  // Content
  title,
  description,
  tooltip,
  icon,
  children,
  
  // Styling
  variant = 'outlined', // 'outlined', 'elevated'
  elevate = true,
  showDivider = true,
  
  // Card parts customization
  headerProps = {},
  contentProps = {},
  
  // Other props
  sx = {},
  ...rest
}) => {
  const theme = useTheme();
  
  // Render the title with icon and tooltip
  const renderTitle = () => {
    if (!title && !icon) return null;
    
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        {/* Icon */}
        {icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: theme.palette.mode === 'dark' 
                ? theme.palette.primary.light 
                : theme.palette.primary.main,
              mr: 0.5,
            }}
          >
            {React.isValidElement(icon) 
              ? React.cloneElement(icon, { fontSize: 'medium' })
              : icon
            }
          </Box>
        )}
        
        {/* Title */}
        {title && (
          <Typography 
            variant="h6" 
            component="span" 
            sx={{ fontWeight: 600 }}
          >
            {title}
          </Typography>
        )}
        
        {/* Tooltip */}
        {tooltip && (
          <Tooltip title={tooltip} arrow placement="top">
            <IconButton 
              size="small" 
              color="primary"
              sx={{ 
                ml: 0.5, 
                p: 0.5,
                '&:hover': {
                  backgroundColor: colorUtils.withOpacity(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <HelpIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    );
  };
  
  // Render description
  const renderDescription = () => {
    if (!description) return null;
    
    return (
      <Typography 
        variant="body2" 
        color="text.secondary"
        sx={{ 
          mt: spacingTokens.component.xs,
          lineHeight: 1.5,
        }}
      >
        {description}
      </Typography>
    );
  };
  
  const hasHeader = title || icon || description;
  
  return (
    <StyledCard
      variant={variant}
      elevate={elevate}
      sx={sx}
      {...rest}
    >
      {/* Header Section */}
      {hasHeader && (
        <StyledCardHeader
          title={renderTitle()}
          subheader={renderDescription()}
          titleTypographyProps={{ variant: 'h6', component: 'div' }}
          {...headerProps}
        />
      )}
      
      {/* Divider */}
      {showDivider && hasHeader && (
        <Divider 
          sx={{ 
            opacity: theme.palette.mode === 'dark' ? 0.1 : 0.15,
          }} 
        />
      )}
      
      {/* Content Section */}
      <StyledCardContent {...contentProps}>
        {children}
      </StyledCardContent>
    </StyledCard>
  );
};

FormSection.propTypes = {
  // Content
  title: PropTypes.node,
  description: PropTypes.node,
  tooltip: PropTypes.string,
  icon: PropTypes.node,
  children: PropTypes.node.isRequired,
  
  // Styling
  variant: PropTypes.oneOf(['outlined', 'elevated']),
  elevate: PropTypes.bool,
  showDivider: PropTypes.bool,
  
  // Card parts customization
  headerProps: PropTypes.object,
  contentProps: PropTypes.object,
  
  // Other props
  sx: PropTypes.object,
};

export default FormSection;